//* 토스 가상계좌 결제 취소 — 환불받을 계좌를 함께 접수해 PG 자동환불로 보낸다.
//* 기존 취소 API(PATCH {role}/shop-order/cancel/:id)에 refundReceiveAccount를 실어 보내는 것뿐이고,
//* 상태 전이(Paid → CancelRequested → 웹훅이 Canceled 확정)는 카드 취소와 똑같다.
//! "환불 완료"라고 쓰면 안 된다 — 토스는 고객 계좌에 실제 입금된 시점을 알려주는 웹훅이 없다.
//! 부트페이 가상계좌·무통장·'계좌 입금'은 이 경로가 아니라 기존 수기환불(OrderManualRefundRequestModal)이다.
//* (스펙: docs/shop-order-vbank-cancel-spec.md)

import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { useRefundBankList } from '@shared/api/shopOrder/refundBank.hook';
import { cancelShopOrder } from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { shopOrderCancelErrorMessage } from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
  //* 목록 페이지 등 카드 밖에서 열었을 때 새로고침 콜백
  onSuccess?: () => void;
}

//* 구서버는 계좌를 보내도 무시하고 토스가 취소를 거절한다 — 400이 아닌 실패는 그쪽도 포함해 안내한다
const CANCEL_FAIL_FALLBACK =
  '취소 요청에 실패했습니다. 계좌 정보를 확인하고, 반복되면 계좌환불(수기이체) 요청으로 진행해주세요.';

export const OrderVbankCancelModal: FC<Props> = function OrderVbankCancelModal({
  openId,
  order,
  onSuccess,
}: Props) {
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  //* 열려 있을 때만 조회 (구서버 404면 프론트 폴백 목록으로 대체된다)
  const bankList = useRefundBankList(open);

  const paymentByCash = order.paymentDetail.paymentByCash ?? 0;
  const orderer = typeof order.user === 'object' ? order.user : undefined;
  //* 계좌환불(수기환불) 요청이 살아 있으면 서버가 400 MANUAL_REFUND_IN_PROGRESS로 막는다 —
  //* 이체까지 끝난 건에 PG 취소가 나가면 고객이 두 번 받기 때문. Rejected는 막지 않는다(자동환불로 처리).
  //* 서버가 최종 방어선이지만, 계좌를 다 채우고 눌러야 실패를 아는 건 낭비라 여기서 먼저 잠근다.
  const manualRefundStatus = order.manualRefund?.status;
  const blockedByManualRefund =
    manualRefundStatus === 'Requested' || manualRefundStatus === 'Done';

  const [cancelReason, setCancelReason] = useState('');
  const [bank, setBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  //* 열리는 순간에만 프리필한다. order를 deps에 넣으면 편집 중 창 포커스 refetch로
  //* 입력한 계좌가 서버값으로 되돌아가 사라진다.
  useEffect(() => {
    if (!open) return;
    setCancelReason('');
    setBank('');
    setAccountNumber('');
    //* 예금주는 주문자 이름으로 채워두되 수정 가능 — 통장 명의가 다른 경우가 흔하다
    setHolderName(orderer?.info?.name ?? order.deliveryDetail.senderName ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const onSubmit = useCallback(async () => {
    if (!cancelReason.trim()) {
      notificateError(notifiacationInstance, '취소 사유를 입력해주세요.');
      return;
    }
    if (!bank || !accountNumber.trim() || !holderName.trim()) {
      notificateError(
        notifiacationInstance,
        '환불계좌(은행·계좌번호·예금주)를 모두 입력해주세요.',
      );
      return;
    }
    //* 서버가 하이픈·공백은 지워주지만, 그 외 문자가 섞이면 토스가 거절한다 — 보내기 전에 잡는다
    if (/[^0-9]/.test(accountNumber.replace(/[\s-]/g, ''))) {
      notificateError(notifiacationInstance, '계좌번호는 숫자와 - 만 입력해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      await cancelShopOrder(
        {
          _id: order._id,
          cancelReason: cancelReason.trim(),
          refundReceiveAccount: {
            bank,
            accountNumber: accountNumber.trim(),
            holderName: holderName.trim(),
          },
        },
        sellerRole(),
      );
      //* 배송관리 목록·과거배송검색 등 주문 조회를 한 번에 갱신 (문자열 키는 prefix 매칭이 안 된다)
      await invalidateQueriesByUrlPart(queryClient, 'shop-order');
      //! 입금 시점은 우리가 알 수 없다 (토스에 입금완료 웹훅이 없음) — "완료"라고 쓰지 말 것
      notificateSuccess(
        notifiacationInstance,
        '취소가 접수되었습니다. 입금까지 영업일 기준 1~2일 걸릴 수 있습니다.',
      );
      onSuccess?.();
      onCloseModal();
    } catch (err) {
      console.error(err);
      notificateError(
        notifiacationInstance,
        shopOrderCancelErrorMessage(err, CANCEL_FAIL_FALLBACK),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    cancelReason,
    bank,
    accountNumber,
    holderName,
    order._id,
    sellerRole,
    queryClient,
    notifiacationInstance,
    onSuccess,
    onCloseModal,
  ]);

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">주문 취소 (가상계좌 환불)</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            토스 가상계좌 결제는 환불받을 계좌를 함께 접수해야 취소됩니다. 접수하면 토스가
            해당 계좌로 환불금을 보냅니다 — 입금까지 영업일 기준 1~2일 걸릴 수 있습니다.
          </p>
        </div>

        {/* 주문 요약 */}
        <div className="rounded-md border bg-gray-50 p-3 text-sm">
          <p className="font-medium">{order.orderDetail?.shopItemEmbed?.name}</p>
          <p className="text-gray-700">
            결제 {paymentByCash.toLocaleString()}원 · {order.paymentDetail.method}
            <span className="text-xs text-gray-500" title={`주문번호: ${order._id}`}>
              {' '}
              · 주문 {order._id.slice(-6)}
            </span>
          </p>
          {orderer?.info?.name && (
            <p className="text-xs text-gray-500">주문자 {orderer.info.name}</p>
          )}
        </div>

        {blockedByManualRefund && (
          <p className="break-keep rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-600">
            계좌환불(수기이체) 요청이
            {manualRefundStatus === 'Done' ? ' 완료된' : ' 접수된'} 주문이라 자동환불이
            막혀 있습니다 — 이체된 돈에 PG 취소까지 나가면 고객이 두 번 받습니다. 자동환불로
            돌리려면 계좌환불 요청 목록(관리자)에서 <b>먼저 그 요청을 반려</b>해주세요.
          </p>
        )}

        {!blockedByManualRefund && (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <label className="w-20 shrink-0 font-medium">취소사유</label>
              <input
                type="text"
                className="w-full rounded border border-gray-300 px-2 py-1"
                placeholder="고객 요청 등"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-20 shrink-0 font-medium">은행</label>
              <select
                className="w-full rounded border border-gray-300 bg-white px-2 py-1"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
              >
                <option value="">은행 선택</option>
                {bankList.map((option) => (
                  <option key={option.code} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-20 shrink-0 font-medium">계좌번호</label>
              <input
                type="text"
                inputMode="numeric"
                className="w-full rounded border border-gray-300 px-2 py-1"
                placeholder="- 없이 입력해도 됩니다"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-20 shrink-0 font-medium">예금주</label>
              <input
                type="text"
                className="w-full rounded border border-gray-300 px-2 py-1"
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
              />
            </div>
            <p className="break-keep text-xs text-gray-500">
              예금주는 통장에 적힌 이름 그대로 — 토스가 실예금주와 대조해 다르면 거절합니다.
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            className="button-rectangle h-10 flex-1 border border-gray-300"
            onClick={onCloseModal}
          >
            닫기
          </Button>
          {!blockedByManualRefund && (
            <Button
              className="button-rectangle h-10 flex-1 border-none bg-warn text-white"
              disabled={submitting}
              onClick={onSubmit}
            >
              {submitting ? '접수 중...' : '취소하고 환불'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
